import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";

import { useDataStore } from "../dataStore";
import { useClientPortal } from "../clientPortal/ClientPortalContext";
import {
  formatClientPortalDate,
  normalizeClientOrders,
} from "../clientPortal/clientPortalNormalizers";
import {
  normalizeRegistrationCities,
  normalizeRegistrationCounties,
} from "../clientPortal/clientPortalRegistration";
import {
  buildClientProfilePayload,
  validateClientProfileForm,
} from "../clientPortal/clientPortalProfile";
import { DEFAULT_BRAND_THEME } from "../themeTokens";

function replaceTokens(text, replacements = {}) {
  return Object.entries(replacements).reduce(
    (result, [key, value]) =>
      result.replaceAll(`{${key}}`, String(value ?? "")),
    String(text || "")
  );
}

const ACCOUNT_DIALOG_PAPER_SX = {
  m: { xs: 1.5, sm: 3 },
  width: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
  height: "auto",
  maxHeight: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
  borderRadius: { xs: "18px", sm: "22px" },
};

function AccountAction({
  icon,
  title,
  description,
  actionLabel,
  onClick,
  theme,
}) {
  const c = { ...DEFAULT_BRAND_THEME, ...theme };

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "18px",
        border: "1px solid rgba(67,20,7,0.09)",
        bgcolor: c.surface,
        boxShadow: "0 10px 28px rgba(33,20,12,0.055)",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 1.5, sm: 1.75 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: { xs: 1.5, sm: 1.75 } },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "13px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(247,181,0,0.15)",
              color: c.text,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography
            component="h3"
            fontWeight={950}
            sx={{ color: c.text, lineHeight: 1.2 }}
          >
            {title}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 1.25,
            mb: 1.5,
            lineHeight: 1.45,
            flexGrow: 1,
          }}
        >
          {description}
        </Typography>
        <Button
          color="inherit"
          variant="outlined"
          fullWidth
          onClick={onClick}
          aria-haspopup="dialog"
          endIcon={<ChevronRightRoundedIcon aria-hidden="true" />}
          sx={{
            minHeight: 42,
            mt: "auto",
            borderRadius: "12px",
            justifyContent: "space-between",
            textTransform: "none",
            fontWeight: 850,
            color: c.text,
            borderColor: "rgba(67,20,7,0.14)",
          }}
        >
          {actionLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

function DialogHeading({ children, onClose, closeDisabled = false }) {
  return (
    <DialogTitle
      sx={{
        py: { xs: 1.75, sm: 2 },
        pl: { xs: 2, sm: 2.5 },
        pr: 7,
        fontWeight: 950,
      }}
    >
      {children}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        disabled={closeDisabled}
        sx={{ position: "absolute", top: { xs: 8, sm: 10 }, right: 8 }}
      >
        <CloseRoundedIcon />
      </IconButton>
    </DialogTitle>
  );
}

function profileForm(profile) {
  return {
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    phone: profile?.phone || "",
    county: profile?.county || null,
    city: profile?.city || null,
    preferences: {
      marketing: Boolean(profile?.preferences?.marketing),
      email: Boolean(profile?.preferences?.email),
      phone: Boolean(profile?.preferences?.phone),
      sms: Boolean(profile?.preferences?.sms),
      promotions: Boolean(profile?.preferences?.promotions),
      news: Boolean(profile?.preferences?.news),
      orderNotifications: Boolean(
        profile?.preferences?.orderNotifications
      ),
      promotionNotifications: Boolean(
        profile?.preferences?.promotionNotifications
      ),
      newsNotifications: Boolean(
        profile?.preferences?.newsNotifications
      ),
      rewardNotifications: Boolean(
        profile?.preferences?.rewardNotifications
      ),
    },
  };
}

function mergeCurrentOption(options, current) {
  if (!current?.id) return options;
  return options.some((option) => String(option.id) === String(current.id))
    ? options
    : [current, ...options];
}

function ProfilePanel({ theme, t }) {
  const {
    profile,
    loadRegistrationCounties,
    loadRegistrationCities,
    updateClientProfile,
    refreshClientProfile,
  } = useClientPortal();
  const { selectedLanguageIref } = useDataStore();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => profileForm(profile));
  const [fieldErrors, setFieldErrors] = useState({});
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!editing) setForm(profileForm(profile));
  }, [editing, profile]);

  useEffect(() => {
    if (!open || !editing) return undefined;
    let active = true;
    setCountiesLoading(true);
    setLoadError(false);
    loadRegistrationCounties()
      .then((raw) => {
        if (!active) return;
        setCounties(normalizeRegistrationCounties(raw));
        setCountiesLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setCounties([]);
        setCountiesLoading(false);
        setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [editing, loadRegistrationCounties, open]);

  useEffect(() => {
    if (!open || !editing || !form.county?.id) {
      setCities([]);
      setCitiesLoading(false);
      return undefined;
    }
    let active = true;
    setCitiesLoading(true);
    setLoadError(false);
    loadRegistrationCities(form.county.id)
      .then((raw) => {
        if (!active) return;
        setCities(normalizeRegistrationCities(raw));
        setCitiesLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setCities([]);
        setCitiesLoading(false);
        setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [editing, form.county?.id, loadRegistrationCities, open]);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
    setSubmitError(false);
  };

  const setPreference = (key, value) => {
    setForm((current) => ({
      ...current,
      preferences: { ...current.preferences, [key]: value },
    }));
    setSubmitError(false);
  };

  const onOpen = () => {
    setForm(profileForm(profile));
    setFieldErrors({});
    setSubmitError(false);
    setSaved(false);
    setEditing(false);
    setOpen(true);
  };

  const onClose = () => {
    if (submitting) return;
    setEditing(false);
    setOpen(false);
  };

  const onEdit = () => {
    setForm(profileForm(profile));
    setFieldErrors({});
    setLoadError(false);
    setSubmitError(false);
    setSaved(false);
    setEditing(true);
  };

  const onSave = async () => {
    if (submitting) return;
    const errors = validateClientProfileForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setSubmitting(true);
    setSubmitError(false);
    try {
      await updateClientProfile(
        buildClientProfilePayload(form, selectedLanguageIref)
      );
      await refreshClientProfile();
      setSaved(true);
      setEditing(false);
    } catch (error) {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const location =
    [profile?.city?.name, profile?.county?.name].filter(Boolean).join(", ") ||
    t("portal.profile.notProvided");

  return (
    <>
      <AccountAction
        icon={<AccountCircleRoundedIcon />}
        title={t("portal.profile.title")}
        description={t("portal.profile.description")}
        actionLabel={t("portal.profile.open")}
        onClick={onOpen}
        theme={theme}
      />

      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: ACCOUNT_DIALOG_PAPER_SX }}
      >
        <DialogHeading onClose={onClose} closeDisabled={submitting}>
          {editing ? t("portal.profile.edit") : t("portal.profile.title")}
        </DialogHeading>
        <DialogContent
          dividers
          sx={{ p: { xs: 1.75, sm: 2.5 } }}
        >
          {editing ? (
            <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ pt: 0.25 }}>
              {loadError ? (
                <Alert severity="warning">
                  {t("portal.common.partialError")}
                </Alert>
              ) : null}
              {submitError ? (
                <Alert severity="error">{t("portal.profile.failed")}</Alert>
              ) : null}
              <Grid container spacing={1.75}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="client-profile-first-name"
                  label={t("portal.register.firstName")}
                  value={form.firstName}
                  onChange={(event) => setField("firstName", event.target.value)}
                  required
                  fullWidth
                  size="small"
                  disabled={submitting}
                  error={Boolean(fieldErrors.firstName)}
                  helperText={
                    fieldErrors.firstName
                      ? t("portal.register.required")
                      : " "
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="client-profile-last-name"
                  label={t("portal.register.lastName")}
                  value={form.lastName}
                  onChange={(event) => setField("lastName", event.target.value)}
                  required
                  fullWidth
                  size="small"
                  disabled={submitting}
                  error={Boolean(fieldErrors.lastName)}
                  helperText={
                    fieldErrors.lastName
                      ? t("portal.register.required")
                      : " "
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("portal.profile.email")}
                  value={profile?.email || ""}
                  fullWidth
                  size="small"
                  disabled
                  helperText={t("portal.profile.emailReadOnly")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="client-profile-phone"
                  label={t("portal.profile.phone")}
                  value={form.phone}
                  onChange={(event) => setField("phone", event.target.value)}
                  autoComplete="tel"
                  fullWidth
                  size="small"
                  disabled={submitting}
                  error={Boolean(fieldErrors.phone)}
                  helperText={
                    fieldErrors.phone
                      ? t("portal.profile.phoneInvalid")
                      : " "
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={mergeCurrentOption(counties, form.county)}
                  value={form.county}
                  onChange={(_event, value) => {
                    setForm((current) => ({
                      ...current,
                      county: value,
                      city: null,
                    }));
                    setFieldErrors((current) => {
                      const next = { ...current };
                      delete next.city;
                      return next;
                    });
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option.id) === String(value.id)
                  }
                  loading={countiesLoading}
                  disabled={submitting || loadError}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("portal.register.county")}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {countiesLoading ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={mergeCurrentOption(cities, form.city)}
                  value={form.city}
                  onChange={(_event, value) => setField("city", value)}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option.id) === String(value.id)
                  }
                  loading={citiesLoading}
                  disabled={
                    submitting || loadError || !form.county || citiesLoading
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("portal.register.city")}
                      error={Boolean(fieldErrors.city)}
                      helperText={
                        fieldErrors.city
                          ? t("portal.register.required")
                          : " "
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {citiesLoading ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              </Grid>

              <Divider />
              <Box>
                <Typography fontWeight={900} sx={{ mb: 1 }}>
                  {t("portal.profile.preferences")}
                </Typography>
                <Grid container>
                  {[
                    ["marketing", "portal.profile.marketing"],
                    ["email", "portal.profile.emailMessages"],
                    ["sms", "portal.profile.smsMessages"],
                    [
                      "orderNotifications",
                      "portal.profile.orderNotifications",
                    ],
                    [
                      "promotionNotifications",
                      "portal.profile.promotionNotifications",
                    ],
                    ["newsNotifications", "portal.profile.newsNotifications"],
                  ].map(([key, label]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(form.preferences[key])}
                            onChange={(event) =>
                              setPreference(key, event.target.checked)
                            }
                            disabled={submitting}
                          />
                        }
                        label={t(label)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {saved ? (
                <Alert severity="success" sx={{ borderRadius: "13px" }}>
                  {t("portal.profile.saved")}
                </Alert>
              ) : null}
              <Box
                sx={{
                  p: { xs: 1.5, sm: 1.75 },
                  borderRadius: "16px",
                  border: "1px solid rgba(67,20,7,0.09)",
                  bgcolor: "rgba(17,17,17,0.018)",
                }}
              >
                <Stack spacing={0.6}>
                  <Typography fontWeight={950}>
                    {profile?.displayName || t("portal.account.fallbackName")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {profile?.email || t("portal.profile.notProvided")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profile?.phone || t("portal.profile.notProvided")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {location}
                  </Typography>
                </Stack>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("portal.profile.emailReadOnly")}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} disabled={submitting}>
                {t("common.close")}
              </Button>
              <Button
                variant="contained"
                onClick={onSave}
                disabled={submitting}
              >
                {submitting
                  ? t("portal.profile.saving")
                  : t("portal.profile.save")}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>
                {t("common.close")}
              </Button>
              <Button
                variant="contained"
                startIcon={<EditRoundedIcon />}
                onClick={onEdit}
              >
                {t("portal.profile.edit")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

function SecurityPanel({ theme, t }) {
  const { profile, requestPasswordReset } = useClientPortal();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sent, setSent] = useState(false);
  const email = String(profile?.email || "").trim().toLowerCase();

  const reset = () => {
    setFailed(false);
    setSent(false);
  };

  const onSubmit = async () => {
    if (submitting || sent || !email) return;
    setSubmitting(true);
    setFailed(false);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (error) {
      if (error?.status === 0 || error?.status >= 500) {
        setFailed(true);
      } else {
        // Unknown and throttled addresses intentionally receive the same UI.
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AccountAction
        icon={<LockRoundedIcon />}
        title={t("portal.security.title")}
        description={t("portal.security.description")}
        actionLabel={t("portal.security.open")}
        onClick={() => {
          reset();
          setOpen(true);
        }}
        theme={theme}
      />

      <Dialog
        open={open}
        onClose={submitting ? undefined : () => setOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: ACCOUNT_DIALOG_PAPER_SX }}
      >
        <DialogHeading
          onClose={() => setOpen(false)}
          closeDisabled={submitting}
        >
          {t("portal.security.change")}
        </DialogHeading>
        <DialogContent
          dividers
          sx={{ p: { xs: 1.75, sm: 2.5 } }}
        >
          <Stack spacing={1.5}>
            {sent ? (
              <>
                <Alert severity="success">
                  {t("portal.forgot.sentTitle")}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  {t("portal.forgot.sentText")}
                </Typography>
              </>
            ) : (
              <>
                {failed ? (
                  <Alert severity="error">{t("portal.forgot.failed")}</Alert>
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  {t("portal.security.resetDescription")}
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "14px",
                    bgcolor: "rgba(17,17,17,0.035)",
                    border: "1px solid rgba(67,20,7,0.08)",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t("portal.profile.email")}
                  </Typography>
                  <Typography fontWeight={850} sx={{ overflowWrap: "anywhere" }}>
                    {email || t("portal.profile.notProvided")}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            {t("common.close")}
          </Button>
          {!sent ? (
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={submitting || !email}
            >
              {submitting
                ? t("portal.forgot.pending")
                : t("portal.forgot.submit")}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
}

function OrdersPanel({ theme, t, locale, locations, sessionVersion }) {
  const { loadOrders } = useClientPortal();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({
    loading: false,
    orders: [],
    error: false,
  });

  const load = React.useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: false }));
    try {
      const raw = await loadOrders();
      setState({
        loading: false,
        orders: normalizeClientOrders(raw),
        error: false,
      });
    } catch (error) {
      setState({ loading: false, orders: [], error: true });
    }
  }, [loadOrders]);

  useEffect(() => {
    if (open) load();
  }, [load, open, sessionVersion]);

  const locationNames = useMemo(
    () =>
      new Map(
        (locations || []).map((location) => [
          String(location.iref),
          location.name,
        ])
      ),
    [locations]
  );

  const formatTotal = (order) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: order.currency,
      }).format(order.total);
    } catch (error) {
      return `${order.total.toFixed(2)} ${order.currency}`;
    }
  };

  return (
    <>
      <AccountAction
        icon={<ReceiptLongRoundedIcon />}
        title={t("portal.orders.title")}
        description={t("portal.orders.description")}
        actionLabel={t("portal.orders.open")}
        onClick={() => setOpen(true)}
        theme={theme}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: ACCOUNT_DIALOG_PAPER_SX }}
      >
        <DialogHeading onClose={() => setOpen(false)}>
          {t("portal.orders.title")}
        </DialogHeading>
        <DialogContent
          dividers
          sx={{ p: { xs: 1.5, sm: 2.5 } }}
        >
          {state.loading ? (
            <Stack
              role="status"
              aria-label={t("portal.common.loading")}
              spacing={1}
            >
              <Skeleton variant="rounded" height={62} />
              <Skeleton variant="rounded" height={62} />
            </Stack>
          ) : state.error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={load}>
                  {t("portal.common.retry")}
                </Button>
              }
            >
              {t("portal.common.sectionError")}
            </Alert>
          ) : state.orders.length === 0 ? (
            <Typography color="text.secondary">
              {t("portal.orders.empty")}
            </Typography>
          ) : (
            <Stack spacing={0.85}>
              {state.orders.map((order) => {
                const locationName =
                  locationNames.get(String(order.locationId)) ||
                  (order.locationId
                    ? replaceTokens(t("portal.orders.location"), {
                        location: order.locationId,
                      })
                    : "");
                const itemText = replaceTokens(
                  t(
                    order.itemCount === 1
                      ? "portal.orders.items.one"
                      : "portal.orders.items.many"
                  ),
                  { count: order.itemCount }
                );
                return (
                  <Stack
                    key={order.id}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    sx={{
                      p: { xs: 1.15, sm: 1.35 },
                      borderRadius: "14px",
                      border: "1px solid rgba(67,20,7,0.09)",
                      bgcolor: "rgba(17,17,17,0.018)",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={950}>
                        {replaceTokens(t("portal.orders.order"), {
                          code: order.code,
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[
                          formatClientPortalDate(order.createdAt, locale),
                          locationName,
                          itemText,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent={{
                        xs: "space-between",
                        sm: "flex-end",
                      }}
                      sx={{ flexShrink: 0 }}
                    >
                      <Chip
                        size="small"
                        color={
                          order.status === 3 || order.status === 8
                            ? "error"
                            : order.status === 5 ||
                                order.status === 6 ||
                                order.status === 7
                              ? "success"
                              : "default"
                        }
                        label={t(`portal.orders.status.${order.status}`)}
                      />
                      <Typography fontWeight={950}>
                        {formatTotal(order)}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={() => setOpen(false)}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function ClientAccountSelfService() {
  const {
    brandTheme = DEFAULT_BRAND_THEME,
    languageCode,
    locations = [],
    t,
  } = useDataStore();
  const { sessionVersion } = useClientPortal();
  const locale = languageCode === "EN" ? "en-GB" : "ro-RO";

  return (
    <Box
      component="section"
      aria-labelledby="client-account-actions-title"
      sx={{
        pt: { xs: 0.5, sm: 1 },
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography
          id="client-account-actions-title"
          component="h2"
          variant="h6"
          fontWeight={950}
          sx={{ color: brandTheme.text || DEFAULT_BRAND_THEME.text }}
        >
          {t("portal.selfService.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t("portal.selfService.description")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 1.25, sm: 1.5 },
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, minmax(0, 1fr))",
          },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ display: "flex", minWidth: 0 }}>
          <ProfilePanel theme={brandTheme} t={t} />
        </Box>
        <Box sx={{ display: "flex", minWidth: 0 }}>
          <SecurityPanel theme={brandTheme} t={t} />
        </Box>
        <Box sx={{ display: "flex", minWidth: 0 }}>
          <OrdersPanel
            theme={brandTheme}
            t={t}
            locale={locale}
            locations={locations}
            sessionVersion={sessionVersion}
          />
        </Box>
      </Box>
    </Box>
  );
}
